export const TAB_ROOTS = ['/', '/clients'] as const;

export type NavigationTransitionType = 'tab' | 'forward' | 'backward' | 'fade';

export const isTabRoot = (pathname: string) =>
  TAB_ROOTS.includes(pathname as (typeof TAB_ROOTS)[number]);

/** Full-screen form routes opened above a parent list/detail screen. */
export const isStackFormPath = (pathname: string) =>
  pathname.includes('/bookings/') ||
  pathname.endsWith('/invite') ||
  pathname.includes('/measurements/') ||
  pathname.includes('/plans/') ||
  pathname.includes('/leads/') ||
  pathname.includes('/tasks/');

/** @deprecated Use isStackFormPath */
export const isModalPath = isStackFormPath;

export const getTabRoot = (pathname: string) => {
  if (pathname === '/') return '/';
  const match = TAB_ROOTS.find((root) => root !== '/' && pathname.startsWith(`${root}/`));
  return match ?? (TAB_ROOTS.includes(pathname as (typeof TAB_ROOTS)[number]) ? pathname : null);
};

export const getNavigationDepth = (pathname: string) => {
  if (isStackFormPath(pathname)) return 3;
  if (pathname === '/settings/advanced') return 3;
  if (pathname.startsWith('/analytics/') && pathname !== '/analytics') return 3;
  if (pathname === '/analytics' || pathname === '/tasks' || pathname === '/settings' || pathname === '/videos') return 2;
  if (pathname === '/new' || pathname === '/profile') return 1;
  if (pathname.startsWith('/clients/') && pathname !== '/clients') return 1;
  return 0;
};

export const getPageShellKey = (pathname: string) => {
  if (pathname.endsWith('/invite')) return '/clients';
  if (pathname.includes('/bookings/')) return '/';
  if (pathname.includes('/measurements/') || pathname.includes('/plans/')) {
    const match = pathname.match(/^(\/clients\/[^/]+)/);
    return match?.[1] ?? pathname;
  }
  if (pathname.includes('/leads/')) return '/clients';
  if (pathname.includes('/tasks/')) return '/tasks';
  if (pathname.startsWith('/analytics/')) return '/analytics';
  return pathname;
};

export const getPageTransitionClass = (pathname: string) => {
  if (isTabRoot(pathname)) return 'page-tab';
  return 'page-stack';
};

export const resolveTargetPath = (to: string | { pathname?: string }, currentPath: string): string => {
  if (typeof to === 'string') {
    if (to.startsWith('/')) return to.split('?')[0].split('#')[0];
    const base = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
    return new URL(to, `http://local${base}`).pathname;
  }

  return to.pathname?.split('?')[0].split('#')[0] ?? currentPath;
};

export const getNavigationTransitionType = (
  fromPath: string,
  toPath: string,
  action: 'PUSH' | 'POP' | 'REPLACE',
): NavigationTransitionType => {
  const fromDepth = getNavigationDepth(fromPath);
  const toDepth = getNavigationDepth(toPath);

  if (action === 'POP' || toDepth < fromDepth) return 'backward';
  if (toDepth > fromDepth) return 'forward';
  if (isTabRoot(fromPath) && isTabRoot(toPath) && fromPath !== toPath) return 'tab';
  return 'fade';
};
